/**
 * EquipmentMergePanel - Drawer for merging two equipment products
 * Source product will be deleted; target product receives combined data
 */
import { useState, useEffect } from 'react';
import {
  Drawer,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Alert,
  Loader,
  Center,
  Divider,
  ScrollArea,
  TextInput,
  Paper,
  Badge,
} from '@mantine/core';
import { IconAlertCircle, IconGitMerge, IconSearch } from '@tabler/icons-react';
import { equipmentCatalogApi } from '@/api/equipmentCatalog';
import type { EquipmentProductWithSources } from '@/types/equipmentCatalog';
import { EquipmentFieldComparisonRow } from './EquipmentFieldComparisonRow';

const MERGEABLE_FIELDS = [
  { name: 'name', label: 'Product Name' },
  { name: 'image_url', label: 'Image URL' },
  { name: 'brand_id', label: 'Brand' },
  { name: 'subcategory_id', label: 'Subcategory' },
] as const;

type MergeableFieldName = (typeof MERGEABLE_FIELDS)[number]['name'];

interface EquipmentMergePanelProps {
  opened: boolean;
  onClose: () => void;
  sourceProductId: string;
  onSuccess: (mergedProductId: string) => void;
}

export function EquipmentMergePanel({
  opened,
  onClose,
  sourceProductId,
  onSuccess,
}: EquipmentMergePanelProps) {
  const [sourceLoading, setSourceLoading] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [sourceProduct, setSourceProduct] = useState<EquipmentProductWithSources | null>(null);
  const [targetProduct, setTargetProduct] = useState<EquipmentProductWithSources | null>(null);
  const [targetIdInput, setTargetIdInput] = useState('');
  const [fieldSelections, setFieldSelections] = useState<Record<string, 'source' | 'target'>>({});

  useEffect(() => {
    if (opened && sourceProductId) {
      setSourceLoading(true);
      setError(null);

      equipmentCatalogApi
        .getProductWithSources(sourceProductId)
        .then((data) => {
          setSourceProduct(data);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load source product');
        })
        .finally(() => {
          setSourceLoading(false);
        });
    }
  }, [opened, sourceProductId]);

  useEffect(() => {
    if (!opened) {
      setSourceProduct(null);
      setTargetProduct(null);
      setTargetIdInput('');
      setFieldSelections({});
      setError(null);
    }
  }, [opened]);

  const handleLoadTarget = async () => {
    const targetId = targetIdInput.trim();
    if (!targetId) {
      setError('Please enter a product ID');
      return;
    }

    if (targetId === sourceProductId) {
      setError('Target cannot be the same as source');
      return;
    }

    setTargetLoading(true);
    setError(null);

    try {
      const data = await equipmentCatalogApi.getProductWithSources(targetId);
      setTargetProduct(data);
      const initialSelections: Record<string, 'source' | 'target'> = {};
      for (const field of MERGEABLE_FIELDS) {
        initialSelections[field.name] = 'target';
      }
      setFieldSelections(initialSelections);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load target product');
    } finally {
      setTargetLoading(false);
    }
  };

  const handleFieldSelect = (fieldName: string, source: string) => {
    setFieldSelections((prev) => ({ ...prev, [fieldName]: source as 'source' | 'target' }));
  };

  const handleConfirmMerge = async () => {
    if (!sourceProduct || !targetProduct) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await equipmentCatalogApi.mergeProducts({
        source_product_id: sourceProduct.id,
        target_product_id: targetProduct.id,
        field_selections: fieldSelections,
      });
      onSuccess(response.merged_product_id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge products');
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldValues = (fieldName: MergeableFieldName) => {
    if (!sourceProduct || !targetProduct) return [];
    return [
      { source: 'source', value: sourceProduct[fieldName], label: 'Source (deleted)' },
      { source: 'target', value: targetProduct[fieldName], label: 'Target (kept)' },
    ];
  };

  const renderSourceInfo = () => {
    if (sourceLoading) {
      return (
        <Center h={100} data-testid="source-loading">
          <Loader size="sm" />
        </Center>
      );
    }

    if (!sourceProduct) return null;

    return (
      <Paper p="md" bg="red.1" withBorder data-testid="source-info">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600}>Source Product</Text>
            <Badge color="red" variant="filled">Will be deleted</Badge>
          </Group>
          <Text size="sm"><strong>ID:</strong> {sourceProduct.id}</Text>
          <Text size="sm"><strong>Name:</strong> {sourceProduct.name}</Text>
          <Text size="sm"><strong>Brand:</strong> {sourceProduct.brand_name || 'N/A'}</Text>
        </Stack>
      </Paper>
    );
  };

  const renderTargetInput = () => {
    if (targetProduct) return null;

    return (
      <Paper p="md" withBorder data-testid="target-input-section">
        <Stack gap="sm">
          <Text fw={600}>Target Product</Text>
          <Text size="sm" c="dimmed">
            Enter the ID of the product you want to merge into.
          </Text>
          <Group gap="sm">
            <TextInput
              placeholder="Enter product ID..."
              value={targetIdInput}
              onChange={(e) => setTargetIdInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && targetIdInput.trim()) {
                  handleLoadTarget();
                }
              }}
              style={{ flex: 1 }}
              data-testid="target-id-input"
            />
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={handleLoadTarget}
              loading={targetLoading}
              data-testid="load-target-button"
            >
              Load
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  };

  const renderTargetInfo = () => {
    if (!targetProduct) return null;

    return (
      <Paper p="md" bg="green.1" withBorder data-testid="target-info">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600}>Target Product</Text>
            <Badge color="green" variant="filled">Will be kept</Badge>
          </Group>
          <Text size="sm"><strong>ID:</strong> {targetProduct.id}</Text>
          <Text size="sm"><strong>Name:</strong> {targetProduct.name}</Text>
          <Text size="sm"><strong>Brand:</strong> {targetProduct.brand_name || 'N/A'}</Text>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => {
              setTargetProduct(null);
              setFieldSelections({});
            }}
          >
            Change target
          </Button>
        </Stack>
      </Paper>
    );
  };

  const renderFieldComparison = () => {
    if (!sourceProduct || !targetProduct) return null;

    return (
      <>
        <Divider label="Field Values" labelPosition="center" />
        <Alert color="blue" variant="light">
          <Text size="sm">
            Select which values to keep. The source product will be deleted and its
            source URLs will be added to the target.
          </Text>
        </Alert>
        <ScrollArea.Autosize mah="calc(100vh - 600px)" offsetScrollbars>
          <Stack gap="xs">
            {MERGEABLE_FIELDS.map((field) => (
              <EquipmentFieldComparisonRow
                key={field.name}
                fieldName={field.name}
                fieldLabel={field.label}
                values={getFieldValues(field.name)}
                selectedSource={fieldSelections[field.name] || 'target'}
                onSelect={(source) => handleFieldSelect(field.name, source)}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </>
    );
  };

  const renderContent = () => {
    if (error && !sourceProduct) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" data-testid="error-state">
          {error}
        </Alert>
      );
    }

    return (
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            onClose={() => setError(null)}
            withCloseButton
            data-testid="error-alert"
          >
            {error}
          </Alert>
        )}

        {renderSourceInfo()}
        {renderTargetInput()}
        {renderTargetInfo()}
        {renderFieldComparison()}

        <Divider />

        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="blue"
            leftSection={<IconGitMerge size={16} />}
            onClick={handleConfirmMerge}
            loading={submitting}
            disabled={!sourceProduct || !targetProduct}
            data-testid="confirm-merge-button"
          >
            Confirm Merge
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group gap="sm">
          <IconGitMerge size={20} />
          <Title order={4}>Merge Products</Title>
        </Group>
      }
      padding="md"
    >
      {renderContent()}
    </Drawer>
  );
}
